import { type UserDetail, createUserSchema, updateUserSchema } from '#shared/types';
import { Banner } from '#client/components/ui/Banner';
import { FormActions } from '#client/components/ui/FormActions';
import { ResourceFormShell } from '#client/components/ui/ResourceFormShell';
import { useFormValues } from '#client/hooks/useFormValues';
import { useResourceForm } from '#client/hooks/useResourceForm';
import { RESOURCE } from '#client/lib/routes';
import { initialUserValues, toUserPayload } from './user-form.logic';
import { ReportSignatureCard, UserAccountCard } from './UserFormSections';

const PATH = RESOURCE.users;

export function UserFormPage() {
  return (
    <ResourceFormShell<UserDetail> path={PATH} noun="user">
      {({ id, record }) => <UserForm id={id} record={record} />}
    </ResourceFormShell>
  );
}

function UserForm({ id, record }: { id?: string | undefined; record?: UserDetail | undefined }) {
  const isEdit = id !== undefined;
  const { values, set } = useFormValues(initialUserValues(record));

  const form = useResourceForm({
    path: PATH,
    id,
    // On edit the password is optional; an empty string is stripped below so the
    // server keeps the existing hash. The password is never sent to the
    // browser, so there is nothing to prefill.
    schema: isEdit ? updateUserSchema : createUserSchema,
    noun: 'User',
  });

  return (
    <form onSubmit={form.handleSubmit(toUserPayload(values, isEdit))} noValidate>
      <div className="c-form-stack">
        {form.formError && <Banner tone="critical">{form.formError}</Banner>}

        <UserAccountCard values={values} onFieldChange={set} errors={form.errors} isEdit={isEdit} />
        <ReportSignatureCard values={values} onFieldChange={set} errors={form.errors} />

        <FormActions
          cancelTo={PATH}
          submitLabel={isEdit ? 'Save changes' : 'Create user'}
          submitting={form.submitting}
        />
      </div>
    </form>
  );
}
